/**
 * Service for fetching and managing SQL topics
 */

import { API_URL } from "../config";

const getStoredToken = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem("token");
  } catch (err) {
    console.warn("Unable to read auth token from localStorage", err);
    return null;
  }
};

const getAuthHeaders = () => {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const authFetch = (url, options = {}) => {
  const headers = { ...(options.headers || {}), ...getAuthHeaders() };
  return fetch(url, { ...options, headers });
};

export const fetchSqlTopics = async () => {
  try {
    const response = await authFetch(`${API_URL}/api/sql-topics`);

    if (!response.ok) {
      throw new Error(`Failed to fetch topics: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching SQL topics:", error);
    throw error;
  }
};

export const saveKnowledgeGraph = async (graphData) => {
  try {
    const response = await authFetch(`${API_URL}/api/knowledge-graph`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(graphData),
    });

    if (!response.ok) {
      throw new Error(`Failed to save graph: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving knowledge graph:", error);
    throw error;
  }
};

export const loadKnowledgeGraph = async (graphId) => {
  try {
    const url = graphId
      ? `${API_URL}/api/knowledge-graph/export?graph_id=${encodeURIComponent(graphId)}`
      : `${API_URL}/api/knowledge-graph/export`;
    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error(`Failed to load graph: ${response.status}`);
    }

    // 1) parse JSON
    const graph = await response.json();

    // 2) inject isStudent flag however you like
    //    here we mark anything with an "@" in its label as a student
    graph.nodes = graph.nodes.map((n) => ({
      ...n,
      data: {
        ...n.data,
        isStudent: /@/.test(n.data.label),
      },
    }));

    return graph;
  } catch (error) {
    console.error("Error loading knowledge graph:", error);
    throw error;
  }
};

export const saveKnowledgeGraphSnapshot = async (graphId, graphData) => {
  try {
    const url = graphId
      ? `${API_URL}/api/knowledge-graph/snapshot?graph_id=${encodeURIComponent(
          graphId
        )}`
      : `${API_URL}/api/knowledge-graph/snapshot`;

    const response = await authFetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(graphData || {}),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Snapshot failed: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving knowledge graph snapshot:", error);
    throw error;
  }
};

// export const uploadMultiplePdfsAndGetGraph = async (formData, onProgress) => {
//   try {
//     const response = await fetch(
//       // `${API_URL}/api/knowledge-graph/from-generic-files`,
//       `${API_URL}/api/knowledge-graph/from-multiple-files`,
//       {
//         method: "POST",
//         credentials: "include",
//         body: formData,
//       }
//     );

//     if (!response.ok) {
//       const errorText = await response.text();
//       throw new Error(
//         `Multi-file upload failed: ${response.status} - ${errorText}`
//       );
//     }

//     return await response.json();
//   } catch (error) {
//     console.error("Error uploading multiple files:", error);
//     throw error;
//   }
// };

export const uploadPdfAndGetGraph = async (formData) => {
  const res = await authFetch(`${API_URL}/api/knowledge-graph/from-pdf`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });
  if (!res.ok) throw new Error("PDF import failed");
  return await res.json();
};

// List available graphs (non-main) from Neo4j
export const listGraphs = async () => {
  const res = await authFetch(`${API_URL}/api/knowledge-graph/graphs`, {
    credentials: "include",
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`List graphs failed: ${res.status} - ${txt}`);
  }
  return await res.json(); // { graphs: [{id, name, node_count, draft_nodes}] }
};

// New: persist materials first (Postgres + disk)
export const uploadMaterials = async (formData) => {
  const res = await authFetch(`${API_URL}/api/materials/upload`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Upload failed: ${res.status} - ${txt}`);
  }
  return await res.json(); // { status, material_ids }
};

export const createSyllabusDraft = async ({ materialId, courseId, graphName }) => {
  const payload = { material_id: materialId };
  if (typeof courseId !== "undefined") {
    payload.course_id = courseId;
  }
  if (typeof graphName === "string") {
    const trimmed = graphName.trim();
    if (trimmed) {
      payload.graph_name = trimmed;
    }
  }
  const res = await authFetch(`${API_URL}/api/knowledge-graph/syllabus/draft`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Create syllabus draft failed: ${res.status} - ${txt}`);
  }
  return await res.json();
};

export const commitSyllabusDraft = async ({ draftId, concepts, graphName }) => {
  const payload = {
    draft_id: draftId,
    concepts: concepts.map((label) => ({ label })),
  };
  if (typeof graphName === "string") {
    const trimmed = graphName.trim();
    if (trimmed) {
      payload.graph_name = trimmed;
    }
  }
  const res = await authFetch(`${API_URL}/api/knowledge-graph/syllabus/commit`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Commit syllabus draft failed: ${res.status} - ${txt}`);
  }
  return await res.json();
};

export const createConceptDraft = async ({ materialId, graphName, description }) => {
  const payload = { material_id: materialId };
  if (typeof graphName === "string") {
    const trimmed = graphName.trim();
    if (trimmed) {
      payload.graph_name = trimmed;
    }
  }
  if (typeof description === "string") {
    const trimmedDesc = description.trim();
    if (trimmedDesc) {
      payload.description = trimmedDesc;
    }
  }
  const res = await authFetch(`${API_URL}/api/knowledge-graph/concepts/draft`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Create concept draft failed: ${res.status} - ${txt}`);
  }
  return await res.json();
};

export const fetchCanonicalConcepts = async () => {
  const res = await authFetch(`${API_URL}/api/knowledge-graph/canonical-concepts`, {
    credentials: "include",
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Fetch canonical concepts failed: ${res.status} - ${txt}`);
  }
  return await res.json();
};

export const commitConceptDraft = async ({ draftId, concepts, anchorLabels, graphName }) => {
  const payload = {
    draft_id: draftId,
    concepts,
  };
  console.log(
    "🔥 FRONTEND: commitConceptDraft() called",
    { draftId, conceptCount: Array.isArray(concepts) ? concepts.length : null, anchorLabels, graphName }
  );
  if (Array.isArray(anchorLabels) && anchorLabels.length) {
    payload.anchor_labels = anchorLabels;
  }
  if (typeof graphName === "string") {
    const trimmed = graphName.trim();
    if (trimmed) {
      payload.graph_name = trimmed;
    }
  }
  console.log("🔥 FRONTEND: POST /api/knowledge-graph/concepts/commit payload=", payload);
  const res = await authFetch(`${API_URL}/api/knowledge-graph/concepts/commit`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Commit concept draft failed: ${res.status} - ${txt}`);
  }
  const data = await res.json();
  console.log("🔥 FRONTEND: RAW commitConceptDraft response data =", data);
  return data;
};

// New: extract a draft graph (generic pipeline) from uploaded material ids
export const extractDraft = async (materialIds, options = {}) => {
  const graphName =
    typeof options.graphName === "string" ? options.graphName.trim() : "";

  const hasOptions =
    options &&
    (options.assessmentId ||
      typeof options.autoTag !== "undefined" ||
      Boolean(graphName));

  const payload = hasOptions
    ? {
        material_ids: materialIds,
        ...(options.assessmentId ? { assessment_id: options.assessmentId } : {}),
        ...(typeof options.autoTag !== "undefined"
          ? { auto_tag: options.autoTag }
          : {}),
        ...(graphName ? { graph_name: graphName } : {}),
      }
    : materialIds;

  const res = await authFetch(`${API_URL}/api/knowledge-graph/extract-draft`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Extract draft failed: ${res.status} - ${txt}`);
  }
  return await res.json(); // { batch_id, nodes, edges, metadata }
};

export const getProgressInsights = async (assessmentId) => {
  const res = await authFetch(
    `${API_URL}/api/insights/progress?assessment_id=${encodeURIComponent(
      assessmentId
    )}`,
    {
      credentials: "include",
    }
  );
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Get progress insights failed: ${res.status} - ${txt}`);
  }
  return await res.json();
};

export const discardDraft = async (batchId) => {
  const res = await authFetch(`${API_URL}/api/knowledge-graph/drafts/${batchId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Delete draft failed: ${res.status} - ${txt}`);
  }
  return await res.json();
};

// List persisted materials (uploaded files)
export const listMaterials = async () => {
  const res = await authFetch(`${API_URL}/api/materials`, {
    credentials: "include",
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`List materials failed: ${res.status} - ${txt}`);
  }
  return await res.json(); // { materials: [{id, filename, content_type, size_bytes, uploaded_at}] }
};

// Fetch graph by relationship type (e.g. HAS_DOCUMENT, MENTIONS, etc.)
export const loadGraphByRelationship = async (relType) => {
  const payload = { types: [relType], direction: "any", limit: 50 };
  const res = await authFetch(`${API_URL}/api/knowledge-graph/query-by-rel`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Query by relationship failed: ${res.status} - ${txt}`);
  }

  const data = await res.json();

  // Normalize node format (like other service functions do)
  data.nodes = (data.nodes || []).map((n) => ({
    ...n,
    properties: n.properties || n.props || {},
  }));

  return data; // { nodes, edges }
};

// --- Reconcile (concepts-with-context, run, commit) ---
export const fetchConceptsWithContext = async ({ limit = 100, graphId, graphName, includeProps = false } = {}) => {
  const qs = new URLSearchParams();
  qs.set("limit", String(limit));
  qs.set("include_props", String(includeProps));
  if (graphId) qs.set("graph_id", graphId);
  if (graphName) qs.set("graph_name", graphName);

  const res = await authFetch(`${API_URL}/api/knowledge-graph/concepts-with-context?${qs.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`concepts-with-context failed: ${res.status} - ${txt}`);
  }
  return await res.json(); // { concepts, source }
};

export const commitReconcileEdges = async ({ edges, draft = false, graphName, batchId, concepts } = {}) => {
  const payload = { edges: edges || [], draft };
  if (graphName) payload.graph_name = graphName;
  if (batchId) payload.batch_id = batchId;
  if (Array.isArray(concepts)) payload.concepts = concepts;

  // Use dedicated edge-commit endpoint
  const res = await authFetch(`${API_URL}/api/knowledge-graph/reconcile/commit`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`reconcile.commit failed: ${res.status} - ${txt}`);
  }
  return await res.json(); // { status, committed_edges, batch_id, ... }
};
